/** Dependencies */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Grid,
  IconButton,
  MenuItem as MenuItemComponent,
  Pagination,
  Select,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Table,
  Tooltip,
  Chip,
  Typography,
  Button,
} from '@mui/material';

/** Components */
import LoadingState from '../../../../components/LoadingState';
import EmptyState from '../../../../components/EmptyState';
import StopWatch from '../../../../components/Timer/StopWatch';
import Icon from '../../../../components/Icon';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

/** Helpers */
import getActiveLanguage from '../../../../helpers/getActiveLanguage';
import LazyImage from '../../../../helpers/LazımImage';
import keyHasPermission from '../../../../helpers/keyHasPermission';
import { singlePriceFormatter } from '../../../../helpers/moneyFormatter';

/** Hooks */
import useOrders from '../../../../hooks/useOrders';
import useProfile from '../../../../hooks/useProfile';

/** Styles */
import generalStyles from '../../../../assets/stylesheets/generalStyles';
import './OrdersTableContent.css';

/** Services */
import { GroupedDataColumnType } from '../../../../services/api/orders/types';

/** SVG */
import Pusher, { Channel } from 'pusher-js';

interface Props {
  selectActive: number;
  page: number;
  handleChange: (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => Promise<void>;
  handlePrintTable: (tableId: string) => Promise<void>;
  handleSetStatus: (
    table_id: string,
    status_id: number,
    isPickedUp?: boolean,
  ) => Promise<void>;
  setType: (value: ((prevState: number) => number) | number) => void;
  setOrder: (
    value:
      | ((
          prevState: GroupedDataColumnType | undefined,
        ) => GroupedDataColumnType | undefined)
      | GroupedDataColumnType
      | undefined,
  ) => void;
  setOrderId: (value: ((prevState: string) => string) | string) => void;
  setOpen: (value: ((prevState: boolean) => boolean) | boolean) => void;
  setDelivererModal: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void;
  setAddPoolModal: (value: ((prevState: boolean) => boolean) | boolean) => void;
  setAddCourierModal: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void;
  setRejectModal: (value: ((prevState: boolean) => boolean) | boolean) => void;
  setApproveModal: (value: ((prevState: boolean) => boolean) | boolean) => void;
  setDelivererId: (value: ((prevState: string) => string) | string) => void;
  setStatusId: (value: ((prevState: number) => number) | number) => void;
  setFastPaymentModal: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void;
  setOpenLastTransactions: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void;
  isShowLastOrders: string;
  setIsShowLastOrders: (
    value: ((prevState: string) => string) | string,
  ) => void;
  isShowAddress: string;
  setIsShowAddress: (value: ((prevState: string) => string) | string) => void;
  groupedOrders: GroupedDataColumnType[] | null;
  meta: any;
  setPreparationAlarmValue: (
    value: ((prevState: number) => number) | number,
  ) => void;
  preparationAlarmValue: number;
}

function OrdersTableHeader({
  selectActive,
  page,
  handleChange,
  handlePrintTable,
  handleSetStatus,
  setType,
  setOrder,
  setApproveModal,
  setPreparationAlarmValue,
  preparationAlarmValue,
  setOrderId,
  setOpen,
  setRejectModal,
  setDelivererModal,
  setAddPoolModal,
  setDelivererId,
  setStatusId,
  setFastPaymentModal,
  setOpenLastTransactions,
  isShowLastOrders,
  setAddCourierModal,
  setIsShowLastOrders,
  isShowAddress,
  setIsShowAddress,
  groupedOrders,
  meta,
}: Props) {
  const { t } = useTranslation('orders');
  const { groupedOrdersLoading, getOrderLastTransactions } = useOrders();
  const navigate = useNavigate();
  const { meProfile } = useProfile();
  const [highlightedRow, setHighlightedRow] = useState<string | null>(null);
  const [cookingAlarmActive, setCookingAlarmActive] = useState<boolean>(false);
  const [courierCokingMinutes, setCourierCokingMinutes] = useState<any>(null);
  let pusher: Pusher | null = null;
  let orderChannel: Channel | null = null;

  const hasSetDelivererPermission = keyHasPermission(
    'set-order-transaction-deliverer',
    meProfile?.permissions || [],
  );

  const selectedOptions = (status_id: number, type_id: number) => {
    if (type_id === 3) {
      if (status_id === 5) {
        return [
          { id: 1, text: t('order.placed') },
          { id: 2, text: t('order.preparing') },
          { id: 3, text: t('order.ready') },
          { id: 5, text: t('order.picked_up') },
          { id: 16, text: t('order.completed') },
        ];
      }

      return [
        { id: 1, text: t('order.placed') },
        { id: 2, text: t('order.preparing') },
        { id: 3, text: t('order.ready') },
        { id: 5, text: t('order.picked_up') },
      ];
    }

    if (type_id === 2) {
      if (status_id === 3) {
        return [
          { id: 1, text: t('order.placed') },
          { id: 2, text: t('order.preparing') },
          { id: 3, text: t('order.ready') },
          { id: 16, text: t('order.completed') },
        ];
      }

      return [
        { id: 1, text: t('order.placed') },
        { id: 2, text: t('order.preparing') },
        { id: 3, text: t('order.ready') },
      ];
    }

    return [
      { id: 1, text: t('order.placed') },
      { id: 2, text: t('order.preparing') },
      { id: 3, text: t('order.ready') },
      { id: 5, text: t('order.picked_up') },
    ];
  };

  const getStatusColor = (status_id: number) => {
    switch (status_id) {
      case 1:
        return '#fbc02d';
      case 2:
        return '#f9a825';
      case 3:
        return '#f57f17';
      case 5:
        return '#66B2FF';
      case 16:
        return '#00c853';
      default:
        return '#00c853';
    }
  };

  const preparationAlarmValueRef = useRef(preparationAlarmValue);

  useEffect(() => {
    preparationAlarmValueRef.current = preparationAlarmValue;
  }, [preparationAlarmValue]);

  const handleCourierLocationUpdated = (r: any) => {
    setCookingAlarmActive(true);
    if (
      groupedOrders &&
      Array.isArray(groupedOrders) &&
      r?.tasks &&
      Array.isArray(r.tasks)
    ) {
      groupedOrders.forEach((order: any) => {
        const matchingTask = r.tasks.find(
          (task: any) => task.integration_id === order.id,
        );

        if (matchingTask) {
          console.log('Matching task', matchingTask);
          console.log('Remaining time', matchingTask.remaining_time);

          if (
            cookingAlarmActive &&
            matchingTask.remaining_time <= preparationAlarmValue
          ) {
            setHighlightedRow(matchingTask.integration_id);
            setCourierCokingMinutes(matchingTask.remaining_time);
          }
        }
      });
    } else {
      console.warn('groupedOrders veya tasks geçerli değil!');
    }
  };

  useEffect(() => {
    let audio: HTMLAudioElement | null = null;

    if (cookingAlarmActive && highlightedRow) {
      audio = new Audio('/cookingAlarm.mp3');
      audio.loop = true;
      audio.play().catch(error => {
        console.error('Error playing audio:', error);
      });

      const interval = setInterval(() => {
        setHighlightedRow(null);
      }, 200000000);

      return () => {
        clearInterval(interval);
        if (audio) {
          audio.pause();
          audio = null;
        }
      };
    }
  }, [cookingAlarmActive, highlightedRow]);

  const closeCookingAlarm = () => {
    setCookingAlarmActive(false);
  };

  useEffect(() => {
    const appKey = process.env.REACT_APP_PUSHER_APP_KEY || '';

    // @ts-ignore
    pusher = new Pusher(appKey, {
      cluster: 'eu',
    });
    orderChannel = pusher.subscribe(`branch.${meProfile?.activeBranch?.id}`);

    if (orderChannel) {
      orderChannel.bind('courier.location.updated', r =>
        handleCourierLocationUpdated(r),
      );
    }

    return () => {
      if (orderChannel) {
        orderChannel.unbind(
          'courier.location.updated',
          handleCourierLocationUpdated,
        );
      }
    };
  }, [
    orderChannel,
    meProfile?.activeBranch?.id,
    preparationAlarmValue,
    groupedOrders,
  ]);

  return (
    <Grid>
      {groupedOrdersLoading ? (
        <Grid alignItems="center" display="flex" justifyContent="center">
          <LoadingState />
        </Grid>
      ) : !groupedOrders?.length ? (
        <Grid alignItems="center" display="flex" justifyContent="center">
          <EmptyState message={t('order.no_order')} />
        </Grid>
      ) : (
        <Table
          sx={{ minWidth: 781, overflowX: 'auto' }}
          aria-label="simple table"
        >
          <TableHead>
            <TableRow>
              <TableCell align="center">{t('order.table.details')}</TableCell>
              <TableCell align="center">
                {t('order.table.customer_details')}
              </TableCell>
              <TableCell align="center">
                {t('order.table.amount_type')}
              </TableCell>
              <TableCell align="center">Adres/Teslimat</TableCell>
              <TableCell align="center">{t('order.table.status')}</TableCell>
              <TableCell align="center">
                {t('order.table.operations')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupedOrders?.map(item => (
              <TableRow
                className={
                  highlightedRow === item.id && cookingAlarmActive
                    ? 'highlighted-row'
                    : ''
                }
              >
                <TableCell>
                  <Grid container justifyContent="center" alignItems="center">
                    <Grid>
                      {item?.integrator?.logo && (
                        <Grid xs={12} ml={4}>
                          {item?.integrator?.logo && (
                            <LazyImage src={item.integrator.logo} width="40" />
                          )}
                        </Grid>
                      )}
                      <Grid
                        xs={12}
                        sx={{
                          textAlign: 'center',
                          fontSize: '14px',
                          fontWeight: '500',
                        }}
                      >
                        {item?.transaction_number} No'lu Sipariş
                      </Grid>
                      <Grid
                        xs={12}
                        sx={{
                          textAlign: 'center',
                          fontSize: '14px',
                          fontWeight: '500',
                        }}
                      >
                        {selectActive === 0 ? (
                          <StopWatch
                            duration={item?.created_at}
                            color="rgb(0, 164, 16)"
                            backgroundColor="white"
                          />
                        ) : (
                          <div>
                            {item.ended_at
                              ? format(
                                  new Date(item.ended_at),
                                  'dd.MM.yyyy HH:mm',
                                )
                              : '_'}
                          </div>
                        )}
                      </Grid>
                      <Grid
                        sx={{
                          color: '#d32f2f',
                          ...generalStyles.generalDisplay,
                          textAlign: 'center',
                          fontSize: '13px',
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          if (item.type_id === 3) {
                            navigate(`/app/pos/order/package/${item.id}`, {
                              state: {
                                tableName: null,
                                full: false,
                              },
                            });
                          }
                          if (item.type_id === 2) {
                            navigate(`/app/pos/order/self/${item.id}`, {
                              state: {
                                tableName: null,
                                full: false,
                              },
                            });
                          }
                        }}
                      >
                        {t('order.order_details')}
                      </Grid>
                    </Grid>
                  </Grid>
                </TableCell>
                <TableCell align="center">
                  {item.customer ? (
                    <Grid>
                      <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                      >
                        {item.customer.order_transactions_count === 1 && (
                          <Grid
                            xs={12}
                            sx={{
                              ...generalStyles.generalDisplay,
                              textAlign: 'center',
                              fontSize: '13px',
                            }}
                          >
                            <Chip
                              sx={{ height: '25px', fontSize: '12px' }}
                              label="Yeni Müşteri"
                              color="success"
                            />
                          </Grid>
                        )}
                        <Grid
                          xs={12}
                          sx={{
                            ...generalStyles.generalDisplay,
                            textAlign: 'center',
                            fontSize: '13px',
                          }}
                        >
                          {`${item?.customer?.first_name || ''} ${
                            item?.customer?.last_name || ''
                          }`}
                        </Grid>
                        <Grid
                          xs={12}
                          sx={{
                            ...generalStyles.generalDisplay,
                            textAlign: 'center',
                            fontSize: '13px',
                          }}
                        >
                          {item?.customer?.phone_number || ''}
                        </Grid>
                      </Grid>
                      <Grid
                        sx={{
                          color: '#d32f2f',
                          ...generalStyles.generalDisplay,
                          textAlign: 'center',
                          fontSize: '13px',
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          if (item.customer) {
                            getOrderLastTransactions(item.customer.id);
                          }
                          setIsShowLastOrders(
                            isShowLastOrders === item.id ? '' : item.id,
                          );
                          setOrder(item);
                          setOpenLastTransactions(true);
                        }}
                      >
                        {t('order.past_order_information')}
                      </Grid>
                    </Grid>
                  ) : (
                    '_'
                  )}
                </TableCell>
                <TableCell align="center">
                  {item.customer ? (
                    <Grid>
                      {item.payments && item.payments.length > 0 ? (
                        <Grid
                          item
                          xs={12}
                          sx={{
                            ...generalStyles.generalDisplay,
                            textAlign: 'center',
                            fontSize: '13px',
                            cursor: 'pointer',
                          }}
                        >
                          {item.payments.map(item2 => (
                            <>
                              {
                                getActiveLanguage(
                                  item2.payment_type.translations,
                                ).name
                              }
                              ({item2.amount.formatted})
                              <br />
                            </>
                          ))}
                        </Grid>
                      ) : (
                        item.preset_payment_type && (
                          <Grid
                            item
                            xs={12}
                            sx={{
                              ...generalStyles.generalDisplay,
                              textAlign: 'center',
                              fontSize: '13px',
                              cursor: 'pointer',
                            }}
                          >
                            {
                              getActiveLanguage(
                                item?.preset_payment_type?.translations,
                              ).name
                            }
                          </Grid>
                        )
                      )}
                      <Grid>
                        {singlePriceFormatter(
                          (
                            Number(item.total_amount.amount) -
                            Number(item.total_discount.amount)
                          ).toString(),
                          1,
                          item.total_amount.currency || 'TRY',
                        ) || '₺0,00'}
                      </Grid>{' '}
                      {Number(item.paid_amount.amount) > 0 && (
                        <Grid color="#d32f2f" ml={1}>
                          | {item.paid_amount.formatted}
                        </Grid>
                      )}
                    </Grid>
                  ) : (
                    '_'
                  )}
                </TableCell>
                <TableCell align="center">
                  <Grid
                    container
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                      fontSize: '13px',
                      marginTop: '15px',
                    }}
                  >
                    {cookingAlarmActive &&
                    item?.type_id !== 2 &&
                    highlightedRow === item.id ? (
                      <Tooltip title="Alarmı Kapat" arrow>
                        <IconButton
                          sx={generalStyles.icon}
                          onMouseDown={e => e.stopPropagation()}
                        >
                          <NotificationsActiveIcon
                            sx={{ color: '#319D37' }}
                            onClick={closeCookingAlarm}
                          />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    {cookingAlarmActive &&
                    item?.type_id !== 2 &&
                    highlightedRow === item.id ? (
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 'bold',
                          mt: 1,
                          textAlign: 'center',
                          color: '#319D37',
                        }}
                      >
                        Kurye Yaklaşıyor: {courierCokingMinutes} dk
                      </Typography>
                    ) : null}
                    {item.deliverer ? (
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 'bold',
                          mt: 1,
                          textAlign: 'center',
                          color: '#319D37',
                        }}
                      >
                        Kurye: {item.deliverer.full_name}
                      </Typography>
                    ) : item.courier_type === 'kuik' &&
                      !item.external_courier ? (
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 'bold',
                          mt: 1,
                          textAlign: 'center',
                          color: '#31929D',
                        }}
                      >
                        Havuza Gönderildi
                      </Typography>
                    ) : null}

                    {item.external_courier && item.courier_type === 'kuik' ? (
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 'bold',
                          mt: 1,
                          textAlign: 'center',
                          color: '#319D37',
                        }}
                      >
                        Havuz Kurye Adı:{' '}
                        {item.external_courier?.name
                          .split(' ')
                          .map((word, index, arr) =>
                            index === arr.length - 1 ? word[0] + '****' : word,
                          )
                          .join(' ')}
                      </Typography>
                    ) : null}
                    {item.external_courier && item.courier_type === 'kuik' ? (
                      <>
                        {(() => {
                          const distance = item?.distance;
                          const partnerPlanFees =
                            meProfile?.activeBranch?.partner_plans?.[0]
                              ?.partner_plan_fees || [];
                          const fee = partnerPlanFees.find(
                            fee =>
                              distance >= fee.start_distance &&
                              distance <= fee.end_distance,
                          );
                          return fee ? (
                            <Typography
                              sx={{
                                fontSize: '13px',
                                mt: 1,
                                textAlign: 'center',
                                color: '#000',
                              }}
                            >
                              Teslimat Ücreti: {fee.customer_price} TL
                            </Typography>
                          ) : null;
                        })()}
                      </>
                    ) : null}

                    {item.external_courier && item.courier_type === 'kuik' ? (
                      <>
                        {(() => {
                          const distance = item?.distance;
                          const partnerPlanFees =
                            meProfile?.activeBranch?.partner_plans?.[0]
                              ?.partner_plan_fees || [];
                          const fee = partnerPlanFees.find(
                            fee =>
                              distance >= fee.start_distance &&
                              distance <= fee.end_distance,
                          );
                          return fee ? (
                            <Typography
                              sx={{
                                fontSize: '13px',
                                mt: 1,
                                textAlign: 'center',
                                color: '#000',
                              }}
                            >
                              Komisyon Ücreti: {fee.commission} TL
                            </Typography>
                          ) : null;
                        })()}
                      </>
                    ) : null}

                    {!item.external_courier &&
                    !item.deliverer &&
                    item?.type_id === 3 &&
                    item.courier_type !== 'kuik' ? (
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 'bold',
                          mt: 1,
                          textAlign: 'center',
                          color: '#d32f2f',
                        }}
                      >
                        Kurye seçimi bekleniyor
                      </Typography>
                    ) : null}
                    {item?.type_id === 2 ? (
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 'bold',
                          mt: 1,
                          textAlign: 'center',
                          color: '#319D37',
                        }}
                      >
                        Gel Al Sipariş
                      </Typography>
                    ) : null}
                    {/* Adres/Teslimat */}
                    {item?.type_id !== 2 ? (
                      <Grid
                        sx={{
                          color: '#d32f2f',
                          cursor: 'pointer',
                          marginTop: '10px',
                          textAlign: 'center',
                          fontSize: '13px',
                        }}
                        onClick={() => {
                          setStatusId(item.status_id);
                          setOrderId(item?.id);
                          setOrder(item);
                          setDelivererId(item?.deliverer?.id || '');
                          setAddCourierModal(true);
                        }}
                      >
                        Adres / Teslimat
                      </Grid>
                    ) : null}
                  </Grid>
                </TableCell>
                <TableCell align="center">
                  {selectActive === 0 ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {!item.company_approved_at ? (
                        <Button
                          variant="contained"
                          sx={{
                            backgroundColor: '#4CAF50', // Yeşil renk
                            color: 'white',
                            borderRadius: '10px',
                            textTransform: 'none', // Yazı küçük harfli kalması için
                            padding: '5px 20px',
                            '&:hover': {
                              backgroundColor: '#45a049',
                            },
                          }}
                          onClick={() => {
                            setApproveModal(true);
                            setOrderId(item.id);
                          }}
                        >
                          Onayla
                        </Button>
                      ) : (
                        <Select
                          variant="filled"
                          SelectDisplayProps={{
                            style: { paddingTop: 10, paddingBottom: 10 },
                          }}
                          sx={{
                            borderRadius: '7px',
                            background: getStatusColor(item.status_id),
                            color: 'white',
                            '.MuiSvgIcon-root ': {
                              color: 'white !important',
                            },
                            '&:hover': {
                              background: '#26acf8',
                            },
                            '& [aria-expanded=true]': {
                              background: getStatusColor(item.status_id),
                              borderRadius: '7px',
                            },
                            '& [aria-expanded=false]': {
                              background: getStatusColor(item.status_id),
                              borderRadius: '7px',
                            },
                            height: '30px',
                            fontSize: '12px',
                          }}
                          displayEmpty
                          disableUnderline
                          value={item.status_id}
                        >
                          {selectedOptions(item.status_id, item.type_id).map(
                            option => (
                              <MenuItemComponent
                                onClick={(event: any) => {
                                  handleSetStatus(item.id, option.id);
                                }}
                                disabled={option.id === item.status_id}
                                key={option.id}
                                value={option.id}
                              >
                                {option.text}
                              </MenuItemComponent>
                            ),
                          )}
                        </Select>
                      )}

                      {!item.company_approved_at ? (
                        <Button
                          sx={{
                            marginLeft: '10px',
                            backgroundColor: '#FFEBEB', // Hafif pembe
                            color: '#D84315', // Kırmızı "X"
                            borderRadius: '8px',
                            minWidth: '36px',
                            height: '36px',
                            '&:hover': {
                              backgroundColor: '#FFD6D6',
                            },
                          }}
                          onClick={() => {
                            setRejectModal(true);
                            setOrderId(item.id);
                          }}
                        >
                          X
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    <Chip
                      sx={{ height: '25px', fontSize: '12px' }}
                      label={
                        selectActive === 1
                          ? t('order.completed')
                          : t('order.cancelled')
                      }
                      color="primary"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Grid container mt={1} justifyContent="flex-end" gap={1}>
                    {hasSetDelivererPermission &&
                      (item?.status_id === 1 || item?.status_id === 2) &&
                      item?.type_id === 3 &&
                      item?.courier_type !== 'kuik' &&
                      !item?.deliverer && (
                        <Tooltip
                          title={t('order.settings.select_deliverer')}
                          arrow
                        >
                          <IconButton
                            sx={generalStyles.icon}
                            onMouseDown={e => e.stopPropagation()}
                            onClick={() => {
                              setStatusId(item.status_id);
                              setOrderId(item?.id || '');
                              setOrder(item);
                              setDelivererId(item?.deliverer?.id || '');
                              setAddCourierModal(true);
                            }}
                          >
                            <Icon name="TwoWheeler" color="error" />
                          </IconButton>
                        </Tooltip>
                      )}
                    <Tooltip title={t('order.details.print')} arrow>
                      <IconButton
                        sx={generalStyles.icon}
                        onMouseDown={e => e.stopPropagation()}
                        onClick={() => handlePrintTable(item.id)}
                      >
                        <Icon name="Print" color="error" />
                      </IconButton>
                    </Tooltip>
                    {selectActive === 0 && (
                      <Tooltip
                        title={t('order.details.fast_transactions')}
                        arrow
                      >
                        <IconButton
                          sx={generalStyles.icon}
                          onMouseDown={e => e.stopPropagation()}
                          onClick={() => {
                            setOpen(true);
                            setOrderId(item?.id || '');
                            setOrder(item);
                            setType(item.status_id);
                          }}
                        >
                          <Icon name="MoreHoriz" color="error" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Grid>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <Pagination
            sx={{ margin: '10px' }}
            count={Math.ceil(
              // @ts-ignore
              meta?.total / meta?.per_page,
            )}
            page={page}
            onChange={handleChange}
          />
        </Table>
      )}
    </Grid>
  );
}

export default OrdersTableHeader;
